import { User } from '.';
import { action, computed, observable, reaction } from 'mobx';
import { app } from './../App';
import {
  IUser,
  IUsersApi,
  IUserCreationInfo,
  UserType,
} from 'common/interfaces/user';

// Store
export class Users {
  constructor(private user: User) {}

  // React to user login change
  private reactToUserLogin = reaction(
    () => this.user.isLoggedIn,
    (loggedIn: boolean) => {
      if (loggedIn) {
        this.fetchAllUsers(true);
      }
    }
  );

  // All users
  @observable public loadingAll: boolean = false;
  @observable public all: IUser[] = [];

  // All users sorted
  @computed public get allUsersSorted(): IUser[] {
    return this.all.sort((a, b) => {
      const aName = a.name ? a.name.toLowerCase() : '0';
      const bName = b.name ? b.name.toLowerCase() : '0';

      return aName > bName ? 1 : aName < bName ? -1 : 0;
    });
  }

  // Sales reps
  @computed public get salesReps(): IUser[] {
    return this.allUsersSorted.filter(
      (user) => user.type === UserType.SalesRep
    );
  }

  // Active sales reps
  @computed public get activeSalesReps(): IUser[] {
    return this.salesReps.filter((user) => user.status);
  }

  // Sales managers
  @computed public get salesManagers(): IUser[] {
    return this.allUsersSorted.filter(
      (user) => user.type === UserType.SalesManager
    );
  }

  // Active sales managers
  @computed public get activeSalesManagers(): IUser[] {
    return this.salesManagers.filter((user) => user.status);
  }

  // Active sales users
  @computed public get allActiveSalesUsers(): IUser[] {
    return [...this.activeSalesReps, ...this.activeSalesManagers].sort(
      (a, b) => {
        const aName = a.name ? a.name.toLowerCase() : '0';
        const bName = b.name ? b.name.toLowerCase() : '0';

        return aName > bName ? 1 : aName < bName ? -1 : 0;
      }
    );
  }

  // Fetch all users
  @action public fetchAllUsers = async (
    activeOnly?: boolean
  ): Promise<IUsersApi> => {
    activeOnly = null;

    try {
      this.loadingAll = true;

      let query: {
        $limit: number;
        $skip: number;
        $sort: { name: 1 | 0 };
        status?: boolean;
      } = {
        $limit: 999999,
        $skip: 0,
        $sort: {
          name: 1,
        },
      };

      if (activeOnly) {
        query = {
          ...query,
          status: true,
        };
      }

      const usersDb: any = app.service('users');
      const users: IUsersApi = await usersDb.find({ query });

      this.all = users.data;

      this.loadingAll = false;

      return users;
    } catch (err) {
      this.loadingAll = false;
      console.error(err);
      return err;
    }
  };

  // Create user
  @action public createUser = async (
    nextUser: IUserCreationInfo
  ): Promise<IUser> => {
    try {
      const usersDb: any = app.service('users');
      const newUsers: IUser = await usersDb.create(nextUser);
      return newUsers;
    } catch (err) {
      console.error(err);
      return err;
    }
  };

  // Fetch single user
  @action public fetchSingleUser = async (id: number): Promise<IUser> => {
    try {
      const userDb: any = app.service('users');
      const user: IUser = (await userDb.get(id)) as IUser;
      return user;
    } catch (err) {
      console.error(err);
      return err;
    }
  };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/stores/Users.ts